import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Sheet } from '../ui/Sheet'
import { Icon } from '../ui/Icon'
import { copyText } from '../ui/clipboard'
import { canShareLink, shareLink } from '../ui/shareLink'
import { BffError, acceptInvite, askInviter, getInviteLink, getSparks, resetInviteLink, sendInvite } from '../transport/relay'
import { runVerb, errText } from './friendVerbs'

/**
 * INVITE FRIENDS — owner, 2026-10-19: 在 side panel Humans 這個欄位, friends 的右側放置一個 Invite 的按鈕 … Invite
 * friend 可以在裡面讓他輸入好友的 email 就可以直接讓他寄出邀請函, 並且幫他把邀請函的草稿也寫好, 當然使用者也有機會
 * 可以去更改一下他的草稿 … Invite Friends 有兩種形式, 一種是使用邀請連結, 一種是使用寄出 email 的方式.
 *
 * And what it earns — Sparks (same day): 每邀請到一個好朋友可以累積十個 Spark, 跟好朋友下棋如果贏的話可以再累積一個
 * Spark. The balance heads the sheet because inviting is how it grows; spending them is designed later.
 */
const SPARKS_KEY = ['sparks'] as const

const DRAFT = `Hi!

I've been using Avengents to work with my AI agents — Claude Code, Codex and others — from my phone or my computer, and to share project rooms with friends and their agents.

Come try it with me. Sign up with this email address and my friend request will be waiting for you.`

/** The server's own sentence for a refusal — it is written to be shown — else the generic one. */
function refusal(e: unknown): { code: string | null; text: string } {
  if (e instanceof BffError) {
    const said = typeof e.body.message === 'string' ? e.body.message : null
    return { code: e.code, text: said ?? errText(e) }
  }
  return { code: null, text: errText(e) }
}

export function InviteSheet({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const sparks = useQuery({ queryKey: SPARKS_KEY, queryFn: getSparks })
  const link = useQuery({ queryKey: ['invite-link'], queryFn: getInviteLink, staleTime: Infinity })
  const [linkNote, setLinkNote] = useState<string | null>(null)
  const [armed, setArmed] = useState(false)
  const [to, setTo] = useState('')
  const [message, setMessage] = useState(DRAFT)
  const [sending, setSending] = useState(false)
  const [mailNote, setMailNote] = useState<{ text: string; ok: boolean } | null>(null)
  /** An address that turned out to be here already: the one thing left to offer is a friend request. */
  const [member, setMember] = useState<string | null>(null)

  const url = link.data?.url ?? ''
  const copy = async () => {
    const r = await copyText(url)
    setLinkNote(r.ok ? 'Link copied' : 'Could not copy — select the link and copy it')
  }
  // Straight from the tap: the share sheet needs the tap's activation (ui/shareLink.ts).
  const share = () => {
    void shareLink(url, 'Join me on Avengents').then((r) => {
      if (r === 'copied') setLinkNote('Link copied')
      else if (r === 'failed') setLinkNote('Could not share — select the link and copy it')
    })
  }

  const send = async () => {
    const email = to.trim()
    setSending(true)
    setMailNote(null)
    setMember(null)
    try {
      await sendInvite(email, message)
      setMailNote({ text: `Invitation sent to ${email}.`, ok: true })
      setTo('')
    } catch (e) {
      const r = refusal(e)
      if (r.code === 'already_member') setMember(email)
      setMailNote({ text: r.text, ok: false })
    } finally {
      setSending(false)
    }
  }

  const befriend = async (email: string) => {
    try {
      await runVerb('request', email)
      setMailNote({ text: `Friend request sent to ${email}.`, ok: true })
      setMember(null)
      void qc.invalidateQueries({ queryKey: ['friend-requests'] })
    } catch (e) {
      setMailNote({ text: refusal(e).text, ok: false })
    }
  }

  const balance = sparks.data?.balance
  return (
    <Sheet title="Invite friends" onClose={onClose} className="mch-sheet inv-sheet">
      <div className="inv-sparks" aria-live="polite">
        <span className="inv-spark-mark" aria-hidden="true">
          <Icon name="auto_awesome" size={18} />
        </span>
        <span className="inv-spark-text">
          <span className="inv-spark-count">
            {balance === undefined ? '…' : balance.toLocaleString()} Spark{balance === 1 ? '' : 's'}
          </span>
          <span className="inv-spark-how">
            Earn 10 for each friend who joins, and 1 for each game you win against a friend. You'll be able to use
            them on Avengents features later.
          </span>
        </span>
      </div>

      <div className="gh-sec">Your invite link</div>
      <div className="grp-search">
        <input
          className="nas-input inv-link"
          readOnly
          value={link.isPending ? 'Making your link…' : link.isError ? 'Could not load your link' : url}
          aria-label="Your invite link"
          onFocus={(e) => e.target.select()}
        />
        <button type="button" className="fr-btn ok" disabled={!url} onClick={() => void copy()}>
          Copy
        </button>
        {canShareLink() && (
          <button type="button" className="fr-btn" disabled={!url} onClick={share}>
            Share…
          </button>
        )}
      </div>
      <span className="grp-sub inv-note">
        Whoever opens it sees your name — never your email address — and can ask to be your friend.
      </span>
      {armed ? (
        <span className="fr-btns">
          <button
            type="button"
            className="fr-btn danger"
            onClick={() => {
              setArmed(false)
              resetInviteLink().then(
                (r) => {
                  qc.setQueryData(['invite-link'], r)
                  setLinkNote('New link made — the old one no longer works.')
                },
                (e) => setLinkNote(refusal(e).text),
              )
            }}
          >
            Reset — the old link stops working
          </button>
          <button type="button" className="fr-btn" onClick={() => setArmed(false)}>
            Cancel
          </button>
        </span>
      ) : (
        <button type="button" className="fr-btn grp-reset" disabled={!url} onClick={() => setArmed(true)}>
          Reset link
        </button>
      )}
      {linkNote && (
        <span className="grp-sub inv-note" role="status">
          {linkNote}
        </span>
      )}

      <div className="gh-sec">Invite by email</div>
      <form
        className="inv-mail"
        onSubmit={(e) => {
          e.preventDefault()
          if (!sending) void send()
        }}
      >
        <input
          className="nas-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="friend@example.com"
          aria-label="Your friend's email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <textarea
          className="gh-report inv-message"
          aria-label="Your message"
          rows={10}
          maxLength={2000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <span className="grp-sub inv-note">
          Your friend sees your name and email address and can reply to you. The invitation adds a link to join and
          a way to stop invitations.
        </span>
        <span className="inv-acts">
          <button type="button" className="fr-btn" disabled={message === DRAFT} onClick={() => setMessage(DRAFT)}>
            Reset draft
          </button>
          <button type="submit" className="fr-btn ok" disabled={sending || !to.trim() || !message.trim()}>
            {sending ? 'Sending…' : 'Send invitation'}
          </button>
        </span>
        {mailNote && (
          <span className={`grp-sub inv-note${mailNote.ok ? ' inv-ok' : ' mch-err'}`} role="status">
            {mailNote.text}
          </span>
        )}
        {member && (
          <button type="button" className="fr-btn ok inv-befriend" onClick={() => void befriend(member)}>
            Send {member} a friend request
          </button>
        )}
      </form>
    </Sheet>
  )
}

/**
 * Signed in after opening someone's invite link (friends/inviteLink.ts): claim it once — their Sparks if this account
 * is new (the BFF decides) — say who it was, and OFFER to ask them to be friends. Offered, never sent for them: a link
 * can be posted anywhere, and the asking is the newcomer's to do (review, 2026-10-19). A link that is not one, or the
 * person's own, closes without a word.
 */
export function InviteWelcome({
  code,
  onClose,
  onOpenHumans,
}: {
  code: string
  onClose: () => void
  onOpenHumans: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState<string | null>(null)
  const [asked, setAsked] = useState<'no' | 'asking' | 'yes' | string>('no')
  useEffect(() => {
    let live = true
    acceptInvite(code).then(
      (r) => {
        if (!live) return
        if (r.self) return onClose()
        setName(r.name || 'A friend')
      },
      () => live && onClose(),
    )
    return () => {
      live = false
    }
    // Once per code: the claim is the server's to make idempotent, and this sheet asks it once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])
  if (name === null) return null
  const ask = () => {
    setAsked('asking')
    askInviter(code).then(
      () => {
        setAsked('yes')
        void qc.invalidateQueries({ queryKey: ['friend-requests'] })
      },
      (e) => setAsked(refusal(e).text),
    )
  }
  return (
    <Sheet
      title="Welcome to Avengents"
      onClose={onClose}
      className="mch-sheet inv-sheet"
      actions={
        asked === 'yes' ? (
          <>
            <button type="button" className="fr-btn" onClick={onClose}>
              Done
            </button>
            <button type="button" className="fr-btn ok" onClick={onOpenHumans}>
              Open Humans
            </button>
          </>
        ) : (
          <>
            <button type="button" className="fr-btn" onClick={onClose}>
              Not now
            </button>
            <button type="button" className="fr-btn ok" disabled={asked === 'asking'} onClick={ask}>
              Send {name} a friend request
            </button>
          </>
        )
      }
    >
      <p className="inv-welcome">
        <strong>{name}</strong> invited you.{' '}
        {asked === 'yes'
          ? `Your request is on its way — once ${name} accepts, you can message each other, share projects and play games.`
          : `Send ${name} a friend request, and once they accept you can message each other, share projects and play games.`}
      </p>
      {asked !== 'no' && asked !== 'asking' && asked !== 'yes' && (
        <p className="grp-sub inv-note mch-err" role="status">
          {asked}
        </p>
      )}
    </Sheet>
  )
}
